/* eslint-disable @typescript-eslint/no-explicit-any */
import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useTeamPlayers } from "@/hooks/api/useLeagues";

interface Props {
  teamId: number;
  gameweekId?: number;
  onClose: () => void;
}

const POSITION_COLORS: Record<string, string> = {
  GK: "bg-yellow-100 text-yellow-800",
  DEF: "bg-blue-100 text-blue-800",
  MID: "bg-green-100 text-green-800",
  FWD: "bg-red-100 text-red-800",
};

export default function TeamPlayersModal({
  teamId,
  gameweekId,
  onClose,
}: Props) {
  const { data: team, isLoading } = useTeamPlayers(teamId, gameweekId);

  return (
    <>
      {/* Overlay — higher z than drawer */}
      <div className="fixed inset-0 bg-black/50 z-60" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-70 p-6">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                {team?.teamName}
              </h3>
              <p className="text-sm text-gray-500">
                {team?.owner?.name} · {team?.formation} ·{" "}
                <span className="font-semibold text-gray-700">
                  {team?.totalPoints} pts
                </span>
                {(team as any)?.gameweek && (
                  <span className="text-blue-500 ml-1">
                    ({(team as any).gameweek.name})
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          {/* Players List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {isLoading
              ? [...Array(11)].map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 rounded-lg animate-pulse"
                  >
                    <div className="w-10 h-6 bg-gray-200 rounded" />
                    <div className="flex-1 h-4 bg-gray-200 rounded" />
                    <div className="w-12 h-4 bg-gray-200 rounded" />
                  </div>
                ))
              : team?.players.map((player: any) => (
                  <div
                    key={player.playerId || player.teamPlayerId}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      player.isCaptain
                        ? "bg-yellow-50 border-yellow-200"
                        : "bg-gray-50 border-transparent"
                    }`}
                  >
                    {/* Flag */}
                    {player.flagUrl ? (
                      <img
                        src={player.flagUrl}
                        className="w-8 h-5 object-cover rounded border shrink-0"
                        alt={player.nation}
                      />
                    ) : (
                      <div className="w-8 h-5 bg-gray-200 rounded shrink-0" />
                    )}

                    {/* Position */}
                    <Badge
                      className={`text-xs w-10 justify-center shrink-0 ${POSITION_COLORS[player.position]}`}
                    >
                      {player.position}
                    </Badge>

                    {/* Name + Nation */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {player.name}
                        </p>
                        {player.isCaptain && (
                          <span className="text-xs bg-yellow-400 text-yellow-900 px-1.5 py-0.5 rounded font-bold shrink-0">
                            C
                          </span>
                        )}
                        {!player.isActive && (
                          <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded shrink-0">
                            Eliminated
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">{player.nation}</p>
                    </div>

                    {/* Jersey */}
                    {player.jerseyNumber && (
                      <span className="text-xs text-gray-400 font-mono shrink-0">
                        #{player.jerseyNumber}
                      </span>
                    )}

                    {/* Points + Stats */}
                    <div className="text-right shrink-0">
                      <span className="text-sm font-bold text-gray-900">
                        {player.points}
                      </span>
                      <span className="text-xs text-gray-400 ml-0.5">pts</span>
                      {/* Gameweek stats breakdown */}
                      {gameweekId && player.stats && (
                        <div className="flex gap-1 mt-0.5 justify-end">
                          {player.stats.goals > 0 && (
                            <span className="text-[9px] bg-green-100 text-green-700 px-1 rounded">
                              {player.stats.goals}G
                            </span>
                          )}
                          {player.stats.assists > 0 && (
                            <span className="text-[9px] bg-blue-100 text-blue-700 px-1 rounded">
                              {player.stats.assists}A
                            </span>
                          )}
                          {player.stats.cleanSheets > 0 && (
                            <span className="text-[9px] bg-purple-100 text-purple-700 px-1 rounded">
                              CS
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
          </div>

          {/* Footer summary */}
          {team && (
            <div className="px-6 py-3 border-t bg-gray-50 flex justify-between text-sm">
              <span className="text-gray-500">
                {team.players.length} players selected
              </span>
              <span className="font-bold text-gray-900">
                Total: {team.totalPoints} pts
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
